/**
 * Search index localStorage persistence layer.
 *
 * Strategy:
 * - On app start: restore cached previews from localStorage into QueryClient (if < 24h old).
 * - After first full load: save all previews to localStorage.
 * - On page return / periodic refresh: fetch N most recent per classification,
 *   merge into QueryClient cache, then re-save to localStorage.
 *
 * localStorage is best-effort — if quota is exceeded (very large orgs), writes
 * silently fail and the cache is memory-only (still works, just won't survive tab close).
 */

import { QueryClient } from '@tanstack/react-query';
import { ApiClient } from '@/services/api-client';
import { Classification, ConversationPreview } from '@/services/types/Lead.types';

const STORAGE_KEY = 'orsay-search-index';
const MAX_AGE = 24 * 60 * 60 * 1000; // 24h

// Exclude Classification.ALL — individual classifications cover all leads.
// Including 'all' breaks mergeRefreshSearchIndex Phase 2 cleanup.
const BUILTIN_CLASSIFICATIONS = Object.values(Classification).filter(
  (c): c is Exclude<Classification, Classification.ALL> => c !== Classification.ALL
);

type PersistedSearchIndex = {
  timestamp: number;
  orgId?: string;
  classifications: Record<string, ConversationPreview[]>;
};

/**
 * Restore search index from localStorage into the QueryClient cache.
 * Called once at app startup (in _app.tsx) before any component mounts.
 * Stores the orgId (if available) so ClientProvider can validate after Clerk loads.
 */
export function restoreSearchIndexFromStorage(queryClient: QueryClient): void {
  if (typeof window === 'undefined') return;

  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return;

    const parsed: PersistedSearchIndex = JSON.parse(raw);
    if (Date.now() - parsed.timestamp > MAX_AGE) {
      localStorage.removeItem(STORAGE_KEY);
      return;
    }

    // Store the orgId the data belongs to so it can be validated later
    if (parsed.orgId) {
      queryClient.setQueryData(['searchIndex.restoredOrgId'], parsed.orgId);
    }

    for (const c of BUILTIN_CLASSIFICATIONS) {
      const previews = parsed.classifications[c];
      if (previews && previews.length > 0) {
        queryClient.setQueryData(['lead.getSearchIndex', c, 'full'], {
          data: { Previews: previews },
        });
      }
    }
    // Restore any custom classification keys
    for (const key of Object.keys(parsed.classifications)) {
      if ((BUILTIN_CLASSIFICATIONS as string[]).includes(key)) continue;
      const previews = parsed.classifications[key];
      if (previews && previews.length > 0) {
        queryClient.setQueryData(['lead.getSearchIndex', key, 'full'], {
          data: { Previews: previews },
        });
      }
    }
  } catch {
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch {
      // Ignore
    }
  }
}

/**
 * Save current search index cache to localStorage.
 * Extracts only the Previews arrays to minimize storage size.
 */
export function saveSearchIndexToStorage(queryClient: QueryClient, orgId?: string): void {
  if (typeof window === 'undefined') return;

  try {
    const classifications: Record<string, ConversationPreview[]> = {};

    for (const c of BUILTIN_CLASSIFICATIONS) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const data = queryClient.getQueryData<any>(['lead.getSearchIndex', c, 'full']);
      const previews = data?.data?.Previews;
      if (previews && previews.length > 0) {
        classifications[c] = previews;
      }
    }

    if (Object.keys(classifications).length === 0) return;

    const persisted: PersistedSearchIndex = {
      timestamp: Date.now(),
      orgId,
      classifications,
    };

    localStorage.setItem(STORAGE_KEY, JSON.stringify(persisted));
  } catch {
    // localStorage full or unavailable — silently ignore
  }
}

/**
 * Remove cached search index from localStorage.
 * Called on org switch to prevent restoring wrong org's data on next page refresh.
 */
export function clearSearchIndexStorage(): void {
  if (typeof window === 'undefined') return;
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Ignore
  }
}

/**
 * Fetch the N most recent conversations per classification and merge into cache.
 * Fresh items override existing entries by LeadID; old non-overlapping items are kept.
 *
 * Cross-classification cleanup: when a lead appears in a fresh fetch for classification X,
 * it is removed from ALL other classification caches. This handles leads whose classification
 * changed between refreshes — no stale duplicates remain.
 *
 * Used for:
 * - On-mount refresh when returning to conversations page (limit=500)
 * - Periodic refresh every 5 min while on page (limit=100)
 */
export async function mergeRefreshSearchIndex(
  queryClient: QueryClient,
  limit: number,
  orgId?: string
): Promise<void> {
  // Phase 1: Fetch all classifications in parallel and merge into each cache
  const freshByClassification = new Map<string, ConversationPreview[]>();

  await Promise.all(
    BUILTIN_CLASSIFICATIONS.map(async (c) => {
      try {
        const fresh = await ApiClient.lead.getSearchIndex({ classification: c, limit });
        const freshPreviews: ConversationPreview[] = fresh.data?.Previews || [];
        if (freshPreviews.length === 0) return;

        freshByClassification.set(c, freshPreviews);

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        queryClient.setQueryData(['lead.getSearchIndex', c, 'full'], (old: any) => {
          if (!old) return { data: { Previews: freshPreviews } };
          const existing: ConversationPreview[] = old.data?.Previews || [];
          const freshIds = new Set(freshPreviews.map((p) => p.LeadID));
          const merged = [...freshPreviews, ...existing.filter((p) => !freshIds.has(p.LeadID))];
          return { ...old, data: { ...old.data, Previews: merged } };
        });
      } catch {
        // Silently ignore — next refresh will retry
      }
    })
  );

  // Phase 2: Cross-classification cleanup — remove leads that moved to another classification.
  // For each classification, collect LeadIDs that appeared in OTHER classifications' fresh data.
  for (const c of BUILTIN_CLASSIFICATIONS) {
    const idsInOtherClassifications = new Set<string>();
    for (const [otherC, previews] of freshByClassification) {
      if (otherC === c) continue;
      for (const p of previews) idsInOtherClassifications.add(p.LeadID);
    }
    if (idsInOtherClassifications.size === 0) continue;

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    queryClient.setQueryData(['lead.getSearchIndex', c, 'full'], (old: any) => {
      if (!old) return old;
      const existing: ConversationPreview[] = old.data?.Previews || [];
      const cleaned = existing.filter((p) => !idsInOtherClassifications.has(p.LeadID));
      if (cleaned.length === existing.length) return old; // nothing to clean
      return { ...old, data: { ...old.data, Previews: cleaned } };
    });
  }

  saveSearchIndexToStorage(queryClient, orgId);
}
